// RichTextEditor - Éditeur de texte enrichi style Microsoft Word
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Toggle } from '../ui/toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Bold, Italic, Underline, Strikethrough, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Code,
  Link, Image, Table, Minus,
  Undo, Redo, Highlighter, Type,
  Subscript, Superscript, RemoveFormatting,
  Indent, Outdent, Palette, ChevronDown,
  FileImage, Upload
} from 'lucide-react';
import { toast } from 'sonner';

// Font families disponibles
const FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Palatino Linotype, serif', label: 'Palatino' },
  { value: 'Garamond, serif', label: 'Garamond' },
  { value: 'Book Antiqua, serif', label: 'Book Antiqua' },
  { value: 'Comic Sans MS, cursive', label: 'Comic Sans MS' },
  { value: 'Impact, sans-serif', label: 'Impact' },
  { value: 'Lucida Console, monospace', label: 'Lucida Console' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: 'Century Gothic, sans-serif', label: 'Century Gothic' },
  { value: 'Cambria, serif', label: 'Cambria' },
  { value: 'Calibri, sans-serif', label: 'Calibri' },
  { value: 'Segoe UI, sans-serif', label: 'Segoe UI' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' }
];

// Font sizes
const FONT_SIZES = [
  { value: '8pt', label: '8' },
  { value: '9pt', label: '9' },
  { value: '10pt', label: '10' },
  { value: '11pt', label: '11' },
  { value: '12pt', label: '12' },
  { value: '14pt', label: '14' },
  { value: '16pt', label: '16' },
  { value: '18pt', label: '18' },
  { value: '20pt', label: '20' },
  { value: '24pt', label: '24' },
  { value: '28pt', label: '28' },
  { value: '32pt', label: '32' },
  { value: '36pt', label: '36' },
  { value: '48pt', label: '48' },
  { value: '72pt', label: '72' }
];

// Heading styles
const HEADING_STYLES = [
  { value: 'p', label: 'Normal' },
  { value: 'h1', label: 'Titre 1' },
  { value: 'h2', label: 'Titre 2' },
  { value: 'h3', label: 'Titre 3' },
  { value: 'h4', label: 'Titre 4' },
  { value: 'h5', label: 'Titre 5' },
  { value: 'h6', label: 'Titre 6' }
];

// Colors
const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
  '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
  '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
  '#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD',
  '#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0',
  '#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79',
  '#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47'
];

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder,
  minHeight = '300px',
  maxHeight = '600px',
  disabled = false,
  onImageUpload // Custom image upload handler
}) => {
  const { language } = useAuth();
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const imageInputRef = useRef(null);

  // Execute command
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  // Format block
  const formatBlock = useCallback((tag) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
  }, []);

  // Handle content change
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Insert link
  const insertLink = useCallback(() => {
    if (!linkUrl) return;
    const text = linkText || linkUrl;
    const html = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    document.execCommand('insertHTML', false, html);
    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
    editorRef.current?.focus();
  }, [linkUrl, linkText]);

  // Insert image from URL
  const insertImageFromUrl = useCallback(() => {
    if (!imageUrl) return;
    const html = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;
    document.execCommand('insertHTML', false, html);
    setShowImageDialog(false);
    setImageUrl('');
    editorRef.current?.focus();
  }, [imageUrl]);

  // Handle image upload
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(language === 'fr' ? 'Sélectionnez une image' : 'Select an image');
      return;
    }

    try {
      let imageUrlToInsert;

      if (onImageUpload) {
        // Custom upload handler
        imageUrlToInsert = await onImageUpload(file);
      } else {
        // Convert to base64 as fallback
        const reader = new FileReader();
        imageUrlToInsert = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      const html = `<img src="${imageUrlToInsert}" alt="${file.name}" style="max-width: 100%; height: auto;" />`;
      document.execCommand('insertHTML', false, html);
      editorRef.current?.focus();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur upload image' : 'Image upload error');
    }
  }, [onImageUpload, language]);

  // Insert table
  const insertTable = useCallback(() => {
    let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    for (let i = 0; i < tableRows; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        const tag = i === 0 ? 'th' : 'td';
        tableHtml += `<${tag} style="border: 1px solid #ccc; padding: 8px; text-align: left;">${i === 0 ? `Col ${j + 1}` : ''}</${tag}>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table><p></p>';
    document.execCommand('insertHTML', false, tableHtml);
    setShowTableDialog(false);
    editorRef.current?.focus();
  }, [tableRows, tableCols]);

  // Toolbar button component
  const ToolbarButton = ({ icon: Icon, onClick, active, tooltip, disabled: btnDisabled }) => (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant={active ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={onClick}
            disabled={disabled || btnDisabled}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Color picker
  const ColorPicker = ({ onSelect, icon: Icon, tooltip }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled}>
          <Icon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <div className="grid grid-cols-10 gap-1">
          {COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: color }}
              onClick={() => onSelect(color)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="border-b bg-muted/30 p-1 flex flex-wrap items-center gap-0.5">
        {/* Undo/Redo */}
        <ToolbarButton icon={Undo} onClick={() => execCommand('undo')} tooltip="Annuler (Ctrl+Z)" />
        <ToolbarButton icon={Redo} onClick={() => execCommand('redo')} tooltip="Rétablir (Ctrl+Y)" />
        
        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Font Family */}
        <Select onValueChange={(val) => execCommand('fontName', val)} disabled={disabled}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Police" />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILIES.map((font) => (
              <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                {font.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Font Size */}
        <Select onValueChange={(val) => execCommand('fontSize', val)} disabled={disabled}>
          <SelectTrigger className="w-16 h-8 text-xs">
            <SelectValue placeholder="12" />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((size) => (
              <SelectItem key={size.value} value={size.value}>
                {size.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Heading Style */}
        <Select onValueChange={(val) => formatBlock(val)} disabled={disabled}>
          <SelectTrigger className="w-24 h-8 text-xs">
            <SelectValue placeholder="Style" />
          </SelectTrigger>
          <SelectContent>
            {HEADING_STYLES.map((style) => (
              <SelectItem key={style.value} value={style.value}>
                {style.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Text formatting */}
        <ToolbarButton icon={Bold} onClick={() => execCommand('bold')} tooltip="Gras (Ctrl+B)" />
        <ToolbarButton icon={Italic} onClick={() => execCommand('italic')} tooltip="Italique (Ctrl+I)" />
        <ToolbarButton icon={Underline} onClick={() => execCommand('underline')} tooltip="Souligné (Ctrl+U)" />
        <ToolbarButton icon={Strikethrough} onClick={() => execCommand('strikeThrough')} tooltip="Barré" />
        <ToolbarButton icon={Subscript} onClick={() => execCommand('subscript')} tooltip="Indice" />
        <ToolbarButton icon={Superscript} onClick={() => execCommand('superscript')} tooltip="Exposant" />
        
        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Colors */}
        <ColorPicker 
          icon={Type} 
          tooltip="Couleur du texte"
          onSelect={(color) => execCommand('foreColor', color)} 
        />
        <ColorPicker 
          icon={Highlighter} 
          tooltip="Surligneur"
          onSelect={(color) => execCommand('hiliteColor', color)} 
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Alignment */}
        <ToolbarButton icon={AlignLeft} onClick={() => execCommand('justifyLeft')} tooltip="Aligner à gauche" />
        <ToolbarButton icon={AlignCenter} onClick={() => execCommand('justifyCenter')} tooltip="Centrer" />
        <ToolbarButton icon={AlignRight} onClick={() => execCommand('justifyRight')} tooltip="Aligner à droite" />
        <ToolbarButton icon={AlignJustify} onClick={() => execCommand('justifyFull')} tooltip="Justifier" />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Lists */}
        <ToolbarButton icon={List} onClick={() => execCommand('insertUnorderedList')} tooltip="Liste à puces" />
        <ToolbarButton icon={ListOrdered} onClick={() => execCommand('insertOrderedList')} tooltip="Liste numérotée" />
        <ToolbarButton icon={Outdent} onClick={() => execCommand('outdent')} tooltip="Diminuer le retrait" />
        <ToolbarButton icon={Indent} onClick={() => execCommand('indent')} tooltip="Augmenter le retrait" />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Insert elements */}
        <Popover open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled}>
              <Link className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">URL</Label>
                <Input 
                  value={linkUrl} 
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{language === 'fr' ? 'Texte (optionnel)' : 'Text (optional)'}</Label>
                <Input 
                  value={linkText} 
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder={language === 'fr' ? 'Texte du lien' : 'Link text'}
                  className="h-8"
                />
              </div>
              <Button onClick={insertLink} size="sm" className="w-full">
                {language === 'fr' ? 'Insérer' : 'Insert'}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Image insertion */}
        <Popover open={showImageDialog} onOpenChange={setShowImageDialog}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled}>
              <Image className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">{language === 'fr' ? 'URL de l\'image' : 'Image URL'}</Label>
                <Input 
                  value={imageUrl} 
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="h-8"
                />
              </div>
              <Button onClick={insertImageFromUrl} size="sm" className="w-full">
                {language === 'fr' ? 'Insérer depuis URL' : 'Insert from URL'}
              </Button>
              <Separator />
              <div>
                <input
                  type="file"
                  ref={imageInputRef}
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button 
                  onClick={() => imageInputRef.current?.click()} 
                  size="sm" 
                  variant="outline"
                  className="w-full gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {language === 'fr' ? 'Télécharger une image' : 'Upload image'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Table insertion */}
        <Popover open={showTableDialog} onOpenChange={setShowTableDialog}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled}>
              <Table className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{language === 'fr' ? 'Lignes' : 'Rows'}</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    max="20"
                    value={tableRows}
                    onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{language === 'fr' ? 'Colonnes' : 'Columns'}</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    max="10"
                    value={tableCols}
                    onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                    className="h-8"
                  />
                </div>
              </div>
              <Button onClick={insertTable} size="sm" className="w-full">
                {language === 'fr' ? 'Insérer tableau' : 'Insert table'}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <ToolbarButton icon={Quote} onClick={() => formatBlock('blockquote')} tooltip="Citation" />
        <ToolbarButton icon={Code} onClick={() => execCommand('formatBlock', 'pre')} tooltip="Code" />
        <ToolbarButton icon={Minus} onClick={() => execCommand('insertHorizontalRule')} tooltip="Ligne horizontale" />
        
        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton 
          icon={RemoveFormatting} 
          onClick={() => execCommand('removeFormat')} 
          tooltip={language === 'fr' ? 'Effacer le formatage' : 'Clear formatting'} 
        />
      </div>

      {/* Editor content area */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onBlur={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        className="p-4 outline-none overflow-y-auto prose prose-sm max-w-none"
        style={{ 
          minHeight, 
          maxHeight,
          fontFamily: 'Arial, sans-serif'
        }}
        data-placeholder={placeholder || (language === 'fr' ? 'Commencez à écrire...' : 'Start typing...')}
      />

      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] table {
          border-collapse: collapse;
          width: 100%;
        }
        [contenteditable] td, [contenteditable] th {
          border: 1px solid #ccc;
          padding: 8px;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6b7280;
        }
        [contenteditable] pre {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
        }
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          margin: 0.5rem 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
